"use client";

import { FC, useEffect, useRef } from "react";
import { MyButton, MyH2 } from "@/components/atoms";
import Image, { StaticImageData } from "next/image";
import heroImage from "../../../assets/images/hero_homepage.jpg";
import paysagiste from "../../../assets/images/paysagiste.png";
import flower from "../../../assets/images/flower.svg";
import leaf from "../../../assets/images/leaf.svg";
import styles from "./styles.module.scss";
import { specialites } from "./data";
import realisation1 from "../../../assets/images/realisation_1.jpg";
import realisation2 from "../../../assets/images/realisation_2.jpg";
import realisation3 from "../../../assets/images/realisation_3.jpg";
import realisation4 from "../../../assets/images/realisation_4.jpg";
import garden from "../../../assets/images/garden.jpg";
import hero_mobile from "../../../assets/images/hero_mobile.jpg";
import arrow_down from "../../../assets/images/arrow_down.gif";
import { GetReviewsApiResponseType } from "@/app/api/reviews/reviews";
import { EstablishmentAPIResponseType } from "@/app/api/establishment/establishment";
import { ContactezNous, Review } from "@/components/templates";
import star from "../../../assets/images/start.svg";
import { uniqueId } from "lodash";
import Link from "next/link";
import classNames from "classnames";
import { MyButtonRef } from "@/components/atoms/my-button/MyButton";

const useFixHeroHomepage = () => {
  useEffect(() => {
    const fixHero = () => {
      const navbar = document.getElementById("navbar-desktop");
      const hero = document.getElementById("hero-homepage");
      const imageHeroDesktop = document.getElementById("image-hero-desktop");
      const arrow = document.getElementById("arrow");
      if (navbar && hero && window.innerWidth >= 1025) {
        const heroHeight = window.innerHeight - navbar.clientHeight;
        hero.style.height = `${heroHeight}px`;
        if (imageHeroDesktop && arrow) {
          imageHeroDesktop.style.height = `${
            heroHeight - arrow.clientHeight
          }px`;
        }
      } else {
        if (hero && imageHeroDesktop) {
          hero.style.height = `auto`;
          imageHeroDesktop.style.height = "auto";
        }
      }
    };
    fixHero();
    window.addEventListener("resize", fixHero);
    return () => {
      window.removeEventListener("resize", fixHero);
    };
  }, []);
};

const FirstBockItem = ({
  img,
  text,
}: {
  img: StaticImageData;
  text: string;
}) => {
  const image = useRef<HTMLImageElement>(null);
  const pargraph = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const fixMarginTop = () => {
      if (image.current && pargraph.current) {
        const img = image.current;
        const _pargraph = pargraph.current;
        _pargraph.style.marginTop = `${img.clientHeight / 2}px`;
        _pargraph.style.marginLeft = `${(65 * img.clientWidth) / 100}px`;
      }
    };
    fixMarginTop();
    window.addEventListener("resize", fixMarginTop);
    return () => window.addEventListener("resize", fixMarginTop);
  }, []);

  return (
    <div className={styles.first_block_item}>
      <Image ref={image} src={img} width={228} height={228} alt={text} />
      <p ref={pargraph}>{text}</p>
    </div>
  );
};

interface SpecialiteItemProps {
  url: string;
  title: string;
  text: string;
  image: StaticImageData;
}

const SpecialiteItem: FC<SpecialiteItemProps> = ({
  url,
  title,
  text,
  image,
}) => {
  const refButton = useRef<MyButtonRef>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  return (
    <Link
      href={url}
      className={classNames(styles.specialiteItem, "block")}
      onMouseOver={() => refButton.current?.setHover()}
      onMouseLeave={() => refButton.current?.setLeave()}
    >
      <div ref={imageContainerRef} className={styles.image_container}>
        <Image src={image} alt={title} width={586} height={262} />
      </div>
      <h3>{title}</h3>
      <div className={styles.text} dangerouslySetInnerHTML={{ __html: text }} />
      <div className={styles.contact_btn}>
        <MyButton
          ref={refButton}
          to={url}
          className={styles.btn}
          label="Découvrir"
        />
      </div>
    </Link>
  );
};

const Homepage = ({
  reviews,
}: {
  reviews: GetReviewsApiResponseType;
  establishment: EstablishmentAPIResponseType;
}) => {
  useFixHeroHomepage();

  return (
    <div className={styles.homepage}>
      <div className={styles.hero_container} id="hero-homepage">
        <div className={styles.images_container}>
          <div className={styles.gradient} />
          <Image
            id="image-hero-desktop"
            className={styles.hero_image_desktop}
            src={heroImage}
            alt="Paysagiste"
          />
          <Image
            id="hero_image_mobile"
            className={styles.hero_image_mobile}
            src={hero_mobile}
            alt="Paysagiste"
          />
          <Image
            className={styles.paysagiste_desktop}
            src={paysagiste}
            width={1800}
            height={484}
            alt="Paysagiste"
          />
        </div>
        <div className={styles.arrow} id="arrow">
          <Image src={arrow_down} className={styles.gif_arrow} alt="Arrow" />
        </div>
      </div>
      <div className={styles.first_block}>
        <h1>Cockenpot paysage, paysagiste de père en fils</h1>
        <div className={styles.contents}>
          {[
            {
              img: flower,
              text: "Fort de plus de 40 années d'expérience, nous sommes à votre disposition pour répondre à vos besoins.",
            },
            {
              img: leaf,
              text: "Nous vous accompagnons pour construire, rénover et entretenir ensemble vos espaces de vie. ",
            },
          ].map(({ img, text }) => (
            <FirstBockItem
              key={uniqueId(`first_block_`)}
              img={img}
              text={text}
            />
          ))}
        </div>
      </div>
      <div className={styles.block_specialite}>
        <MyH2>Nos Spécialités</MyH2>
        <div className={styles.contents}>
          {specialites.map((el) => (
            <SpecialiteItem key={uniqueId(`special_item_`)} {...el} />
          ))}
        </div>
      </div>
      <ContactezNous />
      <div className={styles.block_realisations}>
        <MyH2>Réalisations</MyH2>
        <div className={styles.contents}>
          <Image
            className={styles.image_1}
            src={realisation1}
            alt="réalisations"
          />
          <div className={styles.image_2_container}>
            <Image
              className={styles.image_2}
              src={realisation2}
              alt="réalisations"
            />
          </div>
          <div className={styles.description}>
            <p>
              Le jardin est une pièce à part entière de votre maison. Lieu de
              vie par définition, il vous permet de passer des moments
              inoubliables et riches en émotions. C&apos;est également un refuge
              pour de nombreuses espèces naturelles et un véritable poumon pour
              votre quotidien.
            </p>
            <div className={styles.btn_container}>
              <MyButton to="/realisations" label="Découvrir" />
            </div>
          </div>
          <Image
            className={styles.image_3}
            src={realisation3}
            alt="réalisations"
          />
          <Image
            className={styles.image_4}
            src={realisation4}
            alt="réalisations"
          />
        </div>
      </div>
      <div className={styles.block_histoire}>
        <MyH2>Notre Histoire</MyH2>
        <div className={styles.contents}>
          <Image className={styles.image} src={garden} alt="Notre Histoire" />
          <div className={styles.histoire_text}>
            <div className={styles.bg_white}></div>
            <div className={styles.text_container}>
              <div className={styles.title_h3_container}>
                <h3>De père en fils depuis trois générations</h3>
              </div>
              <div className={styles.paragraphs}>
                <p>
                  Depuis 1978, nous avons toujours eu à cœur la satisfaction de
                  nos clients. Initialement pépinière de production, nous avons
                  gardé notre fibre végétale tout en devenant ensemblier du
                  jardin.
                </p>
                <p>
                  Au cours de ces années, nous avons su développer un
                  savoir-faire et une maîtrise dans de nombreux domaines. Cela
                  nous permet de vous accompagner dans vos projets, de la
                  conception à la réalisation.
                </p>
                <p>
                  Une entreprise, c&apos;est aussi et surtout une équipe ! Chez
                  nous, l&apos;humain est primordial. Confiance, développement
                  personnel, responsabilisation, bonne humeur... Travaillons
                  sérieusement, oui (évidemment) mais avec le sourire s&apos;il
                  vous plaît !
                </p>
              </div>
              <div className={styles.btn_container}>
                <MyButton to="/a-propos" label="Rencontrez notre équipe" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.reviews_container}>
        <div className={styles.content_header}>
          <MyH2>Les avis de nos clients</MyH2>
          <p>
            Qui de mieux que vous pour parler de votre expérience avec Cockenpot
            Paysage !
          </p>
          <div className={styles.moyen_container}>
            <div className={styles.moyen}>
              <Image src={star} alt="star" />
              <p>
                {(
                  reviews.reduce(
                    (prev, current) => prev + current.global_rate,
                    0
                  ) / reviews.length
                ).toFixed(1)}
                /10 ({reviews.length} avis)
              </p>
            </div>
          </div>
        </div>
        <Review reviews={reviews} />
      </div>
    </div>
  );
};

export default Homepage;
